import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Download,
  IndianRupee,
  Users,
  ScrollText,
  TrendingUp,
  Calendar,
  ShoppingBag,
} from "lucide-react";
import Sparks from "@/components/Sparks";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const TOKEN_KEY = "mantrana_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY) || "";
    } catch (_) {
      return "";
    }
  });
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const headers = token ? { "X-Admin-Password": token } : {};

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/overview`, { headers });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 503) {
        toast.error("Invalid admin credentials");
        try {
          localStorage.removeItem(TOKEN_KEY);
        } catch (_) {}
        setToken("");
      } else {
        toast.error("Could not load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchOverview();
     
  }, [token]);

  const login = async (e) => {
    e?.preventDefault();
    if (!pw) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pw });
      const tk = res.data?.token || pw;
      try {
        localStorage.setItem(TOKEN_KEY, tk);
      } catch (_) {}
      setToken(tk);
      setPw("");
    } catch (e) {
      toast.error(
        e?.response?.status === 401 ? "Invalid password" : "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (_) {}
    setToken("");
    setData(null);
  };

  const exportData = async (kind, format) => {
    try {
      const url = `${API}/admin/export?kind=${kind}&format=${format}`;
      const res = await axios.get(url, {
        headers,
        responseType: format === "csv" ? "blob" : "json",
      });
      const blob =
        format === "csv"
          ? res.data
          : new Blob([JSON.stringify(res.data, null, 2)], {
              type: "application/json",
            });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `mantrana_${kind}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${kind}.${format}`);
    } catch (_) {
      toast.error("Export failed");
    }
  };

  if (!token) {
    return (
      <div
        className="min-h-screen cosmic-bg flex items-center justify-center px-6"
        data-testid="admin-login"
      >
        <Sparks />
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative w-full max-w-sm rounded-3xl p-8 md:p-10"
          style={{
            background:
              "linear-gradient(160deg, rgba(19,25,43,0.92), rgba(35,29,80,0.92))",
            border: "1px solid rgba(212,175,55,0.3)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
          }}
        >
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase gold-text mb-6">
            <Lock className="w-3 h-3" /> Admin Access
          </div>
          <h1 className="font-serif text-3xl text-white font-light mb-2">
            Mantrana Admin
          </h1>
          <p className="text-sm text-slate-400 mb-6 font-light">
            Enter the admin password to access the dashboard.
          </p>
          <input
            type="password"
            placeholder="Password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
            data-testid="admin-password-input"
          />
          <button
            type="submit"
            disabled={loading || !pw}
            className="mt-4 w-full rounded-full py-3 text-sm font-medium cta-gold disabled:opacity-50"
            data-testid="admin-login-button"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen cosmic-bg" data-testid="admin-dashboard">
      <Sparks />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10">
        <header className="flex items-center justify-between mb-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase gold-text mb-1">
              Mantrana
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white font-light">
              Admin Dashboard
            </h1>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-white transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </header>

        {loading && !data ? (
          <div className="text-slate-400 font-light text-sm">Loading…</div>
        ) : data ? (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-kpis">
              <Kpi label="Total Users" value={data.totals.total_users} icon={<Users />} />
              <Kpi label="Free Readings" value={data.totals.free_readings} icon={<ScrollText />} />
              <Kpi label="Paid Users" value={data.totals.paid_users} icon={<ShoppingBag />} />
              <Kpi
                label="Revenue"
                value={`₹${data.totals.revenue_inr.toLocaleString("en-IN")}`}
                icon={<IndianRupee />}
              />
              <Kpi
                label="Conversion"
                value={`${data.totals.conversion_rate_percent}%`}
                icon={<TrendingUp />}
              />
              <Kpi label="DAU" value={data.totals.daily_active_users} icon={<Calendar />} />
              <Kpi label="Consultations" value={data.totals.consultations} icon={<ScrollText />} />
              <Kpi
                label="Revenue / Paid User"
                value={`₹${data.totals.revenue_per_paid_user_inr}`}
                icon={<IndianRupee />}
              />
            </div>

            {/* Exports */}
            <div className="flex flex-wrap gap-3 mb-10" data-testid="admin-export-buttons">
              {[
                "consultations",
                "purchases",
                "usage",
                "events",
              ].map((kind) => (
                <div key={kind} className="inline-flex items-center gap-1">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-slate-400 mr-1">
                    {kind}
                  </span>
                  {["csv", "json"].map((fmt) => (
                    <button
                      key={fmt}
                      onClick={() => exportData(kind, fmt)}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border border-white/15 hover:bg-white/5 text-white"
                      data-testid={`export-${kind}-${fmt}`}
                    >
                      <Download className="w-3 h-3" />
                      {fmt}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            {/* Two-column lists */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Panel title="Recent consultations" testid="admin-recent-consultations">
                {(data.recent_consultations || []).length === 0 && (
                  <Empty>No consultations yet.</Empty>
                )}
                <ul className="space-y-3">
                  {(data.recent_consultations || []).map((c) => (
                    <li
                      key={c.id}
                      className="rounded-xl p-3"
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="font-serif text-base text-white truncate">
                          {c.birth?.full_name || "Anonymous seeker"}
                        </div>
                        <div className="text-[10px] tracking-[0.2em] uppercase gold-text shrink-0">
                          {c.category}
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 font-light mt-1 line-clamp-2">
                        {c.challenge}
                      </div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mt-2">
                        {new Date(c.created_at).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel title="Top categories" testid="admin-top-categories">
                {(data.top_categories || []).length === 0 && (
                  <Empty>No category data yet.</Empty>
                )}
                <ul className="space-y-2">
                  {(data.top_categories || []).map((c) => (
                    <li key={c.category} className="flex items-center justify-between text-sm">
                      <span className="text-white">{c.category}</span>
                      <span className="gold-text font-serif">{c.count}</span>
                    </li>
                  ))}
                </ul>
                <h3 className="font-serif text-base text-white mt-6 mb-3">
                  Recent purchases
                </h3>
                {(data.recent_purchases || []).length === 0 ? (
                  <Empty>No purchases yet.</Empty>
                ) : (
                  <ul className="space-y-2">
                    {(data.recent_purchases || []).map((p) => (
                      <li
                        key={p.id || p.rzp_payment_id || `${p.session_id}-${p.created_at}`}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-white truncate">
                          {p.pack_id} · {p.questions}Q
                        </span>
                        <span className="gold-text font-serif">
                          ₹{p.price_inr}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>
          </>
        ) : (
          <div className="text-slate-400 font-light text-sm">No data</div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon }) {
  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background:
          "linear-gradient(160deg, rgba(19,25,43,0.92), rgba(35,29,80,0.92))",
        border: "1px solid rgba(255,255,255,0.06)",
        boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
      }}
      data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] tracking-[0.25em] uppercase text-slate-400">
          {label}
        </span>
        <span className="text-[#D4AF37]" style={{ opacity: 0.7 }}>
          {icon && <span className="inline-block w-3.5 h-3.5">{icon}</span>}
        </span>
      </div>
      <div className="font-serif text-2xl text-white">{value}</div>
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <div
      className="rounded-2xl p-5 md:p-6"
      style={{
        background:
          "linear-gradient(160deg, rgba(19,25,43,0.92), rgba(35,29,80,0.92))",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
      data-testid={testid}
    >
      <h2 className="font-serif text-lg text-white mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-xs text-slate-500 font-light italic">{children}</p>;
}
